// pages/LargeBitcoinChartPage.tsx
import React from "react";
import BtcPriceChart from "../../components/largebitcoinchart/BitcoinLineChart";
import { getBTCData, ThreeYearChange } from "../../api/getBTCData";
import { WatercolorRuler } from "../../components/largebitcoinchart/WatercolorRuler";

type Point = { dateISO: string; label: string; price: number };

export default function LargeBitcoinChartPage() {
  const [series, setSeries] = React.useState<Point[]>([]);
  const [threeYearData, setThreeYearData] = React.useState<ThreeYearChange[]>([]);
  const [data, setData] = React.useState<{ minRow: ThreeYearChange | null; maxRow: ThreeYearChange | null }>();
  const [indexByDate, setIndexByDate] = React.useState<Map<string, number>>(new Map());

  const [windowSteps, setWindowSteps] = React.useState<number>(0);
  const [startIndex, setStartIndex] = React.useState(0);

  React.useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      try {
        const res = await getBTCData();
        if (ignore || !res?.rates) return;

        // Build canonical series once
        const canonical: Point[] = res.rates
          .map((e: any) => {
            const price = Number.parseFloat(String(e.closePrice));
            const dateISO = String(e.date);
            return {
              dateISO,
              label: new Intl.DateTimeFormat(undefined, {
                month: "short",
                day: "2-digit",
                year: "2-digit",
              }).format(new Date(dateISO)),
              price,
            };
          })
          .filter((d) => Number.isFinite(d.price));

        setSeries(canonical);

        const map = new Map<string, number>();
        canonical.forEach((d, i) => map.set(d.dateISO, i));
        setIndexByDate(map);

        setThreeYearData(res.rows);
        setData({ minRow: res.min || null, maxRow: res.max || null });
      } catch (e) {
        console.error("Failed to load BTC data", e);
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, []);

  // Compute exact step count for 3 years based on the canonical index map
  React.useEffect(() => {
    if (!series.length || !threeYearData.length || !indexByDate.size) return;
  
    for (let i = 0; i < threeYearData.length; i++) {
      const r = threeYearData[i];
      if (!r.endDate) continue;
      const s = indexByDate.get(r.startDate);
      const e = indexByDate.get(r.endDate);
      if (s != null && e != null && e > s) {
        const steps = e - s;
        setWindowSteps(steps);

        setStartIndex(0);
  
        return;
      }
    }
  }, [series, threeYearData, indexByDate]);

  // Clamp when series or windowSteps change
  React.useEffect(() => {
    if (!series.length || !windowSteps) return;
    const maxStart = Math.max(0, series.length - 1 - windowSteps);
    if (startIndex > maxStart) setStartIndex(maxStart);
  }, [series.length, windowSteps, startIndex]);

  const endIndex = Math.min(series.length - 1, startIndex + windowSteps);

  return (
    <div className="w-full rounded-2xl bg-neutral-900 p-4 shadow-lg">
      <BtcPriceChart
        data={series}                 // canonical series
        height={360}
        highlight={{ startIndex, endIndex }}
      />

      {series.length > 0 && windowSteps > 0 && threeYearData.length > 0 && (
        <div className="mt-4">
          <WatercolorRuler
            dates={series.map((s) => s.dateISO)} // same order/length as chart
            threeYearData={threeYearData as any}
            data={data}
            startIndex={startIndex}
            onChange={setStartIndex}             // live updates
            windowSteps={windowSteps}
          />
        </div>
      )}
    </div>
  );
}
